import { useState } from "react";
import { useEffect } from "react";
import { connect } from "react-redux";

import AssignmentRoundedIcon from "@mui/icons-material/AssignmentRounded";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import { alpha, useTheme } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import formatISO from "date-fns/formatISO";

import { assessmentTemplates } from "../../assets/assessments";
import { createAssessmentTemplateData } from "../../assets/dataSchema";
import { deepCopyObject } from "../../utils/helpers";
import PracticeAssessmentTemplateDialog from "../dialogs/practiceAssessmentTemplateDialog";
import OmniAvatar from "../ui/omniAvatar";
import OmniEditButton from "../ui/omniEditButton";
import OmniResponsiveButton from "../ui/omniResponsiveButton.js";
import Widget from "../ui/widget";
import WidgetList from "../ui/widgetList";
import WidgetSkeleton from "../ui/widgetSkeleton";
import WidgetTitle from "../ui/widgetTitle";

const PracticeAssessmentTemplates = props => {
  const { userODM } = props;

  const initializeAssessmentTypes = assessmentTemplates => {
    return deepCopyObject(assessmentTemplates);
  };

  const [practiceAssessmentTemplates, setPracticeAssessmentTemplates] = useState(
    initializeAssessmentTypes(assessmentTemplates),
  );
  const [selectedAssessment, setSelectedAssessment] = useState(null);

  const updateAssessmentTemplates = assessmentTemplate => {
    // Find the assessment
    let assessmentTemplateIndex = practiceAssessmentTemplates.findIndex(
      template => template._id === assessmentTemplate._id,
    );

    if (assessmentTemplateIndex >= 0) {
      //We're modifying here
      assessmentTemplate.modifiedBy = userODM.omniData.displayName;
      assessmentTemplate.modifiedDate = formatISO(new Date());
      assessmentTemplate.isActive = true;

      setPracticeAssessmentTemplates(prevState =>
        prevState.map(template =>
          template._id === assessmentTemplate._id ? (template = assessmentTemplate) : template,
        ),
      );
    } else {
      //We're creating here
      assessmentTemplate.createdBy = userODM.attributes.sub;
      assessmentTemplate.createdDate = formatISO(new Date());
      assessmentTemplate.isActive = true;

      setPracticeAssessmentTemplates(prevState => [...prevState, assessmentTemplate]);
    }
  };

  const [titleText, setTitleText] = useState(null);

  useEffect(() => {
    if (practiceAssessmentTemplates != null) {
      setTitleText(
        `${practiceAssessmentTemplates.length} Template${
          practiceAssessmentTemplates.length === 1 ? `` : `s`
        }`,
      );
    } else setTitleText("");
  }, [practiceAssessmentTemplates]);

  // if (practiceAssessmentTemplates == null && practiceAssessments == null) {
  //   return <Widget>{/* add skeleton? */}</Widget>;
  // }

  const theme = useTheme();

  return (
    <Widget>
      <WidgetTitle
        title={"Assessments"}
        text={titleText}
        loading={practiceAssessmentTemplates == null}
      >
        <OmniResponsiveButton
          tooltipTitle="Add a assessment template for this practice"
          buttonText="Assessment"
          onClick={() => {
            setSelectedAssessment(createAssessmentTemplateData());
          }}
        />
      </WidgetTitle>

      {practiceAssessmentTemplates == null && <WidgetSkeleton />}
      {practiceAssessmentTemplates != null && (
        <WidgetList>
          {practiceAssessmentTemplates.map((assessmentTemplate, index) => {
            return (
              <ListItem
                key={`assessment${index}`}
                disableGutters={true}
                sx={{
                  padding: theme.spacing(1),
                  paddingTop: 0,
                  paddingBottom: 0,
                  marginBottom: theme.spacing(1),
                  borderRadius: theme.spacing(1),
                  backgroundColor: alpha(theme.palette.info.main, 0.75),
                  color: theme.palette.info.contrastText,
                  boxShadow: theme.shadows[3],
                  userSelect: "none",
                  webkitUserSelect: "none",
                }}
              >
                <Tooltip title={"No Notes"} placement="right">
                  <OmniAvatar
                    sx={{
                      width: 36,
                      height: 36,
                      fontSize: "1.8rem",
                      border: `2px solid ${theme.palette.info.contrastText}`,
                    }}
                  >
                    <AssignmentRoundedIcon />
                  </OmniAvatar>
                </Tooltip>
                <ListItemText
                  primary={
                    <Typography
                      variant="h6"
                      sx={{
                        marginLeft: theme.spacing(1),
                        marginRight: theme.spacing(3),
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        color: theme.palette.info.contrastText,
                        marginTop: 0,
                      }}
                    >
                      {assessmentTemplate.omniData.title}
                    </Typography>
                  }
                  secondary={
                    <Typography
                      variant="body2"
                      sx={{
                        marginLeft: theme.spacing(1),
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        marginTop: "-6px",
                        color: theme.palette.info.contrastText,
                        minimumHeight: 20,
                      }}
                    >
                      {`${assessmentTemplate.omniData.questions.length} Question${
                        assessmentTemplate.omniData.questions.length === 1 ? `` : `s`
                      }`}
                    </Typography>
                  }
                />
                <OmniEditButton
                  onClick={e => {
                    e.stopPropagation();
                    setSelectedAssessment(assessmentTemplate);
                  }}
                />
              </ListItem>
            );
          })}
        </WidgetList>
      )}

      {selectedAssessment && (
        <PracticeAssessmentTemplateDialog
          open={selectedAssessment != null}
          dialogTitle={`${selectedAssessment.id == null ? "Add" : "Edit"} Assessment`}
          initialValues={deepCopyObject(selectedAssessment)}
          onDelete={() => {
            setSelectedAssessment(null);
          }}
          onAccept={selectedAssessment => {
            updateAssessmentTemplates(selectedAssessment);
            setSelectedAssessment(null);
          }}
          onClose={() => {
            setSelectedAssessment(null);
          }}
        />
      )}
    </Widget>
  );
};

const mapStateToProps = state => ({
  userODM: state.userSession.userODM,
});

const mapDispatchToProps = {};

export default connect(mapStateToProps, mapDispatchToProps)(PracticeAssessmentTemplates);
